// Shared tab strip for period navigation: current month first, past months
// collapsed under a dropdown. Consumed by Paid Registrations, Interest
// Forms, and any future registration surface.

import { useEffect, useMemo, useState } from "react";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import {
  getRegistrationPeriodNavigation,
  registrationTabLabel,
  subscribeToDayRollover,
  type PeriodCount,
  type RegistrationTabKey,
} from "@/lib/registrationPeriodNav";


interface Props {
  counts: PeriodCount[];
  value: RegistrationTabKey | null;
  onChange: (period: RegistrationTabKey) => void;
  /** Called whenever the current-month default recomputes (e.g. rollover). */
  onDefault?: (period: RegistrationTabKey | null) => void;
}

export function PeriodTabStrip({ counts, value, onChange, onDefault }: Props) {
  const [tick, setTick] = useState(0);
  useEffect(() => subscribeToDayRollover(() => setTick((n) => n + 1)), []);

  const nav = useMemo(
    () => getRegistrationPeriodNavigation(counts, new Date()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [counts, tick],
  );

  useEffect(() => {
    onDefault?.(nav.defaultPeriod);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav.defaultPeriod]);

  const active = value ?? nav.defaultPeriod;
  const collapsed = [...nav.past, ...nav.tests];
  const activeCollapsed = collapsed.find((p) => p.period === active);

  const renderGroup = (items: typeof nav.past, label: string) =>
    items.length > 0 ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8">
            {label} <ChevronDown className="h-3.5 w-3.5 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[240px]">
          {items.map((p) => (
            <DropdownMenuItem key={p.period} onSelect={() => onChange(p.period)}>
              <span className="flex-1">{p.label}</span>
              <span className="text-xs text-muted-foreground ml-2">
                {p.count} registration{p.count === 1 ? "" : "s"}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <TabsList className="flex flex-wrap h-auto gap-1 p-1">
        {nav.main.map((p) => (
          <TabsTrigger
            key={p.period}
            value={p.period}
            className="whitespace-normal text-left data-[state=active]:font-semibold"
          >
            {registrationTabLabel(p.period)}{" "}
            <span className="ml-1 text-xs text-muted-foreground">({p.count})</span>
          </TabsTrigger>
        ))}
        {activeCollapsed && (
          <TabsTrigger value={active!} className="whitespace-normal text-left">
            {activeCollapsed.label}{" "}
            <span className="ml-1 text-xs text-muted-foreground">
              ({activeCollapsed.count})
            </span>
          </TabsTrigger>
        )}
      </TabsList>
      {renderGroup(nav.past, "Archived / Historical")}
      {renderGroup(nav.tests, "Test records")}
    </div>
  );
}


